import {ApiClient} from '@hconnect/apiclient'
import {StoppageKind, StoppageType} from '@hconnect/common/types'
import {DateRange} from '@hconnect/uikit/src/common'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useEffect} from 'react'

import {mapStoppageCodes} from '../mappers'
import {
  AggregatedStoppageCodeStatistics,
  DurationInterval,
  IssueGroupingKey,
  TimeStep
} from '../types'
import {prepareAggregatedStoppageCodePayload} from '../utils'

const QueryKey = 'stoppage-code-stats'
const INTERVAL_PERIOD_IN_MS = 300000

type Options<TSelect> = Omit<
  UseQueryOptions<AggregatedStoppageCodeStatistics, AxiosError, TSelect>,
  'queryKey' | 'queryFn'
> & {
  onError?: (error: AxiosError) => void
}

export const useStoppageCodeStats = <TSelect = AggregatedStoppageCodeStatistics>(
  plantId: string,
  payload: DateRange<Date> & {
    groupBy: IssueGroupingKey
    selectedStoppedMainEquipments?: string[]
    selectedMainEquipments?: string[]
    selectedEquipments?: string[]
    selectedStoppages?: StoppageKind[]
    selectedStoppageCodes?: string[]
    timeStep?: TimeStep
    stoppageType?: StoppageType[]
    durationInterval?: DurationInterval
  },
  options?: Options<TSelect>
) => {
  const timezone = useUpmPlantTimezone(plantId)

  const query = useQuery<AggregatedStoppageCodeStatistics, AxiosError, TSelect>({
    queryKey: [QueryKey, plantId, payload, timezone],
    queryFn: async ({signal}) => {
      const response = await ApiClient.axiosInstance.get<AggregatedStoppageCodeStatistics>(
        `/downtime-dashboard/${plantId}/stoppages/stoppage-code-statistics`,
        {
          params: prepareAggregatedStoppageCodePayload({
            to: payload.to,
            from: payload.from,
            stoppedDepartmentUpmIds: payload.selectedStoppedMainEquipments,
            mainEquipmentNumbers: payload.selectedMainEquipments,
            equipmentNumbers: payload.selectedEquipments,
            kinds: payload.selectedStoppages,
            stoppageCodes: mapStoppageCodes(payload.selectedStoppageCodes),
            groupBy: payload.groupBy,
            timeStep: payload.timeStep,
            stoppageType: payload.stoppageType,
            durationInterval: payload.durationInterval
          }),
          signal
        }
      )

      return response.data
    },
    staleTime: 1000 * 60 * 15,
    refetchInterval: INTERVAL_PERIOD_IN_MS,
    ...options
  })

  const onError = options?.onError
  useEffect(() => {
    if (query.error) {
      onError?.(query.error)
    }
  }, [query.error, onError])

  return query
}
